import React, {useCallback, useContext, useEffect, useState} from 'react';
import axios from 'axios';
import moment from 'moment';
import {Link, useNavigate} from 'react-router-dom';
import withAuth from '../../auth';
import {API_URL} from '../../constants';
import {AuthContext} from '../AuthContext';
import Pagination from '../Pagination';
import WhitelabelBadge from '../WhitelabelBadge';
import WhitelabelFilter from '../WhitelabelFilter';
import {
    closeInvoicePdfWindow,
    navigateInvoicePdfWindow,
    openInvoicePdfWindow,
} from './invoicePdfWindow';
import {AXIOS_NOT_FOUND_REDIRECT_OPT_OUT} from '../../axiosNotFoundRedirect';

const compactFilters = (filters) => Object.fromEntries(
    Object.entries(filters).filter(([, value]) => (
        value !== '' && value !== null && value !== undefined
    ))
);

export const getInvoiceErrorMessage = (error, fallback) => {
    const validationErrors = error?.response?.data?.errors;

    if (validationErrors) {
        return Object.values(validationErrors)
            .flatMap((messages) => Array.isArray(messages) ? messages : [messages])
            .filter(Boolean)
            .join(' ');
    }

    return error?.response?.data?.message || fallback;
};

export const InvoiceList = () => {
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [sortBy, setSortBy] = useState('id');
    const [sortOrder, setSortOrder] = useState('desc');
    const [filters, setFilters] = useState({});
    const [appliedFilters, setAppliedFilters] = useState({});
    const navigate = useNavigate();
    const {token, setToken, hasPermission} = useContext(AuthContext);
    const canViewCustomers = Boolean(hasPermission?.('user-view'));

    const fetchInvoices = useCallback(async () => {
        setLoading(true);
        setError('');

        try {
            const response = await axios.post(
                `${API_URL}/api/admin/invoices`,
                {
                    sort_by: sortBy,
                    sort_order: sortOrder,
                    page: currentPage,
                    ...appliedFilters,
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            setInvoices(response.data.data || []);
            setTotalPages(response.data.last_page || 1);
        } catch (requestError) {
            setInvoices([]);
            setTotalPages(1);

            if (requestError?.response?.status === 401) {
                localStorage.removeItem('authToken');
                setToken(null);
                navigate('/');
            } else {
                setError(getInvoiceErrorMessage(
                    requestError,
                    'An error occurred while loading invoices. Please try again later.'
                ));
            }
        } finally {
            setLoading(false);
        }
    }, [appliedFilters, currentPage, navigate, setToken, sortBy, sortOrder, token]);

    useEffect(() => {
        fetchInvoices();
    }, [fetchInvoices]);

    const handleSortChange = (column) => {
        if (sortBy === column) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(column);
            setSortOrder('asc');
        }

        setCurrentPage(1);
    };

    const handleFilterChange = (event, column) => {
        setFilters((currentFilters) => compactFilters({
            ...currentFilters,
            [column]: event.target.value,
        }));
    };

    const handleSearchSubmit = () => {
        setCurrentPage(1);
        setAppliedFilters(compactFilters(filters));
    };

    const handleDropdownChange = (event, column) => {
        const newFilters = compactFilters({
            ...filters,
            [column]: event.target.value,
        });

        setFilters(newFilters);
        setCurrentPage(1);
        setAppliedFilters(newFilters);
    };

    const statusColors = {
        open: '#FFF4CC',
        paid: '#D1F2CF',
        void: '#FFD6D6',
    };

    const formatBillingPeriod = (invoice) => {
        if (!invoice.billing_period_start || !invoice.billing_period_end) return '—';

        return `${moment(invoice.billing_period_start).format('YYYY-MM-DD')} → ${moment(
            invoice.billing_period_end
        ).format('YYYY-MM-DD')}`;
    };

    const openPdf = async (invoice) => {
        setError('');
        const pdfWindow = openInvoicePdfWindow();

        if (!pdfWindow) {
            setError('Unable to open the invoice PDF. Please allow pop-ups and try again.');
            return;
        }

        try {
            const response = await axios.get(
                `${API_URL}/api/admin/invoices/${invoice.id}/pdf`,
                {
                    ...AXIOS_NOT_FOUND_REDIRECT_OPT_OUT,
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (!navigateInvoicePdfWindow(pdfWindow, response.data?.url)) {
                closeInvoicePdfWindow(pdfWindow);
                setError('Invoice PDF not found.');
            }
        } catch (requestError) {
            closeInvoicePdfWindow(pdfWindow);

            if (requestError?.response?.status === 401) {
                localStorage.removeItem('authToken');
                setToken(null);
                navigate('/');
            } else {
                setError(getInvoiceErrorMessage(requestError, 'Failed to fetch the invoice PDF.'));
            }
        }
    };

    const sortIcon = (column) => `fa fa-sort${
        sortBy === column ? (sortOrder === 'asc' ? '-asc' : '-desc') : ''
    }`;

    return (
        <div className="container-fluid mt-3">
            <h2 className="text-center">Invoice List</h2>

            {error && <div className="alert alert-danger" role="alert">{error}</div>}

            {loading ? (
                <div
                    className="d-flex justify-content-center align-items-center"
                    style={{height: '80vh'}}
                >
                    <div className="spinner-border" role="status">
                        <span className="sr-only">Loading...</span>
                    </div>
                </div>
            ) : (
                <div className="table-responsive">
                    <table className="table table-sm table-striped text-center text-nowrap">
                        <thead>
                        <tr>
                            <th onClick={() => handleSortChange('invoice_number')}>
                                Invoice # <i className={sortIcon('invoice_number')}/>
                            </th>
                            <th>Whitelabel</th>
                            <th onClick={() => handleSortChange('user_id')}>
                                Customer <i className={sortIcon('user_id')}/>
                            </th>
                            <th onClick={() => handleSortChange('status')}>
                                Status <i className={sortIcon('status')}/>
                            </th>
                            <th onClick={() => handleSortChange('issue_date')}>
                                Issue Date <i className={sortIcon('issue_date')}/>
                            </th>
                            <th onClick={() => handleSortChange('due_date')}>
                                Due Date <i className={sortIcon('due_date')}/>
                            </th>
                            <th>Billing Period</th>
                            <th onClick={() => handleSortChange('total')}>
                                Total <i className={sortIcon('total')}/>
                            </th>
                            <th onClick={() => handleSortChange('items_count')}>
                                Deliveries <i className={sortIcon('items_count')}/>
                            </th>
                            <th>PDF</th>
                        </tr>
                        <tr>
                            <td>
                                <div className="input-group">
                                    <input
                                        aria-label="Filter by invoice number"
                                        className="form-control form-control-sm text-center"
                                        type="text"
                                        inputMode="search"
                                        value={filters.invoice_number || ''}
                                        placeholder="Invoice #"
                                        onChange={(event) => handleFilterChange(event, 'invoice_number')}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter') handleSearchSubmit();
                                        }}
                                    />
                                    <button
                                        aria-label="Search invoice number"
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={handleSearchSubmit}
                                    >
                                        <i className="fa fa-search"/>
                                    </button>
                                </div>
                            </td>
                            <td>
                                <WhitelabelFilter
                                    value={filters.whitelabel_id || ''}
                                    onChange={(event) => handleDropdownChange(event, 'whitelabel_id')}
                                />
                            </td>
                            <td>
                                <div className="input-group">
                                    <input
                                        aria-label="Filter by customer ID"
                                        className="form-control form-control-sm text-center"
                                        type="text"
                                        inputMode="search"
                                        value={filters.user_id || ''}
                                        placeholder="Customer ID"
                                        onChange={(event) => handleFilterChange(event, 'user_id')}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter') handleSearchSubmit();
                                        }}
                                    />
                                    <button
                                        aria-label="Search customer ID"
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={handleSearchSubmit}
                                    >
                                        <i className="fa fa-search"/>
                                    </button>
                                </div>
                            </td>
                            <td>
                                <select
                                    aria-label="Filter by invoice status"
                                    className="form-control form-control-sm text-center"
                                    value={filters.status || ''}
                                    onChange={(event) => handleDropdownChange(event, 'status')}
                                >
                                    <option value="">All</option>
                                    <option value="open">Open</option>
                                    <option value="paid">Paid</option>
                                    <option value="void">Void</option>
                                </select>
                            </td>
                            <td/>
                            <td/>
                            <td/>
                            <td/>
                            <td/>
                            <td/>
                        </tr>
                        </thead>
                        <tbody className="text-wrap">
                        {invoices.map((invoice) => (
                            <tr key={invoice.id}>
                                <td>
                                    <Link to={`/invoices/${invoice.id}`}>
                                        {invoice.invoice_number}
                                    </Link>
                                </td>
                                <td>
                                    <WhitelabelBadge
                                        whitelabelId={invoice.whitelabel_id}
                                        whitelabel={invoice.whitelabel}
                                    />
                                </td>
                                <td>
                                    {invoice.user && (canViewCustomers ? (
                                        <Link to={`/users/${invoice.user.id}`}>
                                            {invoice.user.id}
                                            <br/>
                                            {invoice.user.full_name}
                                        </Link>
                                    ) : (
                                        <span>
                                            {invoice.user.id}
                                            <br/>
                                            {invoice.user.full_name}
                                        </span>
                                    ))}
                                </td>
                                <td style={{backgroundColor: statusColors[invoice.status] || 'transparent'}}>
                                    {invoice.status}
                                </td>
                                <td>
                                    {invoice.issue_date
                                        ? moment(invoice.issue_date).format('YYYY-MM-DD')
                                        : '—'}
                                </td>
                                <td>
                                    {invoice.due_date
                                        ? moment(invoice.due_date).format('YYYY-MM-DD')
                                        : '—'}
                                </td>
                                <td>{formatBillingPeriod(invoice)}</td>
                                <td>{invoice.currency || 'CAD'} {invoice.total}</td>
                                <td>{invoice.items_count ?? invoice.pickup_count ?? '—'}</td>
                                <td>
                                    <button
                                        aria-label={`Download PDF for invoice ${invoice.invoice_number}`}
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() => openPdf(invoice)}
                                    >
                                        PDF
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {!error && invoices.length === 0 && (
                            <tr>
                                <td colSpan="10" className="py-4 text-muted">No invoices found.</td>
                            </tr>
                        )}
                        </tbody>
                    </table>

                    {totalPages > 1 && currentPage <= totalPages && (
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            handlePageChange={setCurrentPage}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

export default withAuth(InvoiceList);
